import React from 'react';
import StatusPill, { PillTone } from '../StatusPill';
import './workflow-status-pill.scss';

// =======================================
// INTERFACES
// =======================================

interface WorkflowStatusPillProps {
  /** The workflow status name, e.g. "QA Review". */
  status: string;
  className?: string;
}

// =======================================
// HELPERS
// =======================================

/**
 * The status's colour class: its name, trimmed, lower-cased and hyphenated
 * ("QA Review" -> "qa-review"). workflow-status-pill.scss holds the palette
 * keyed on the same slugs, so the stylesheet is the one list of statuses;
 * a name it has no rule for simply keeps the grouped tone underneath.
 * Normalised because legacy status strings vary in case and spacing.
 */
const statusSlug = (statusName: string): string =>
  statusName.trim().toLowerCase().replace(/\s+/g, '-');

/**
 * The grouped fallback colour for a workflow status name, for a status the
 * palette does not list yet (a newly added column), so it is never unstyled.
 * Matched loosely; order matters: "Design" is tested after the won/complete
 * groups because "Design Development" and "Schematic Design" both contain it.
 */
export const statusTone = (statusName: string | null | undefined): PillTone => {
  const s = (statusName || '').toLowerCase();
  if (!s) return 'neutral';
  if (s.includes('unsuccessful')) return 'danger';
  if (s.includes('complete') || s.includes('archived')) return 'neutral';
  if (s.includes('probable') || s.includes('preliminary')) return 'warning';
  if (
    s.includes('construction') ||
    s.includes('inspection') ||
    s.includes('qa') ||
    s.includes('final invoice') ||
    s.includes('defect') ||
    s.includes('active')
  ) {
    return 'success';
  }
  return 'info';
};

// =======================================
// MAIN COMPONENT
// =======================================

/**
 * A workflow status as a dot pill, one colour per status, the same on the
 * kanban board, the project modal and the registers. Always use this for a
 * workflow status, never StatusPill with statusTone: the grouped tones make
 * Active, Inspections Invoice, QA Review and Final Invoice one green.
 */
const WorkflowStatusPill: React.FC<WorkflowStatusPillProps> = ({ status, className = '' }) => (
  <StatusPill
    label={status}
    tone={statusTone(status)}
    className={`status-pill--wf-${statusSlug(status)} ${className}`.trim()}
  />
);

export default WorkflowStatusPill;
